// The All accounts table's columns (go-live): the board's own # · Fit · Engmt far left (with their
// hovers), Account, then whose it is, the AE, Vertical, Relationship, Why now, Usage, and the row's
// action. Every column but the action reuses the board's definition (`boardV2Columns.tsx`); only
// Owner, Sprint / pool, FTE and the action are this page's own. Sprint / pool shows only while some
// row is in a pool (a sprint is a pool).

import type { BookColumn } from './columns';
import type { RankedAccount } from './derived';
import type { OwnerState } from './allAccountsFilters';
import { sprintAndPoolText } from './allAccountsFilters';
import { isSprint, sprintWeekText, sprintsOf } from './sprints';
import type { RowAction } from './allAccountsActions';
import { boardV2Columns, Dash } from './boardV2Columns';
import { EMPLOYEE_COUNT_COLUMN } from './sortOptions';

// A board row plus what this page decides for it.
export type AllAccountsRow = RankedAccount & {
  owner_state: OwnerState;
  owner_name: string;
  row_action: RowAction | null;
  action_busy: boolean;
};

function asRow(row: RankedAccount): AllAccountsRow {
  return row as AllAccountsRow;
}

const OWNER_STATE_ORDER: Record<OwnerState, number> = {
  available: 0, strategist: 1, sdr: 2, ae: 3, retired: 4, unowned: 5,
};

const OWNER_STATE_TAG: Record<OwnerState, string> = {
  available: 'Available', strategist: 'Strategist', sdr: 'SDR', ae: 'AE', retired: 'Retired', unowned: 'No owner',
};

const OWNER_STATE_TONE: Record<OwnerState, string> = {
  available: 'good', strategist: 'neutral', sdr: 'neutral', ae: 'neutral', retired: 'neutral', unowned: 'neutral',
};

function ownerText(row: AllAccountsRow): string {
  if (row.owner_state === 'available' || row.owner_state === 'retired') return OWNER_STATE_TAG[row.owner_state];
  return row.owner_name ? `${row.owner_name} (${OWNER_STATE_TAG[row.owner_state]})` : OWNER_STATE_TAG[row.owner_state];
}

// An available account is a green pill; an owned one is the owner's first name. The state pill
// shows only when there is no name to show (retired, no owner).
function OwnerCell({ row }: { row: AllAccountsRow }) {
  const firstName = row.owner_name.trim().split(/\s+/)[0] ?? '';
  const showName = row.owner_state !== 'available' && row.owner_state !== 'retired' && firstName !== '';
  return (
    <span className="aaowner" title={ownerText(row)}>
      {showName
        ? <span className="aaownername">{firstName}</span>
        : <span className={'pill ' + OWNER_STATE_TONE[row.owner_state]}>{OWNER_STATE_TAG[row.owner_state]}</span>}
    </span>
  );
}

const OWNER_COLUMN: BookColumn = {
  key: 'owner_state',
  label: 'Owner',
  width: '104px',
  hint: 'Available (in the bank), or who owns it: a strategist, another SDR, or an AE.',
  // The state first (Available on top), then the owner's name within it.
  sortValue: row => String(OWNER_STATE_ORDER[asRow(row).owner_state]) + asRow(row).owner_name.toLowerCase(),
  accessor: row => ownerText(asRow(row)),
  cell: row => <OwnerCell row={asRow(row)} />,
};

// The sprint an account is in, then the source list(s) of its other pools, for example
// "Fin Serv (US loans)" or "SF prospect · Q4 list". One column rather than two: it never drops,
// where a separate Sprint column was the first thing a 1440 screen lost.
const POOL_COLUMN: BookColumn = {
  key: 'pools',
  label: 'Sprint / pool',
  width: '150px',
  hint: 'The outbound sprint this account is in, else the source list(s) it was banked from (e.g. the November pool). Hover for the week and the pools.',
  sortValue: row => sprintAndPoolText(row) || null,
  accessor: row => sprintAndPoolText(row),
  cell: row => {
    const text = sprintAndPoolText(row);
    const weeks = sprintsOf(row).map(sprint => `${sprint.label}: ${sprintWeekText(sprint)}`);
    const pools = (row.pools ?? []).filter(pool => !isSprint(pool)).map(pool => `${pool.label}: ${pool.sources.join(', ')}`);
    return text ? <span className="aapool" title={[...weeks, ...pools].join('\n')}>{text}</span> : <Dash />;
  },
};

// The button, or a muted label that says on hover why there is none. Clicks inside never open
// the row's panel: the table opens it on a click that is not on a link or a button, and this
// wrapper stops the rest.
function ActionCell({ row, onAct }: { row: AllAccountsRow; onAct: (accountId: string) => void }) {
  const action = row.row_action;
  if (!action) return <Dash />;
  return (
    <span className="aaaction" onClick={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()}>
      {action.enabled ? (
        <button type="button" className="btn aaactionbtn" title={action.note} disabled={row.action_busy}
          aria-label={`${action.label} ${row.account}`}
          onClick={() => onAct(row.id)}>
          {row.action_busy ? 'Sending…' : action.label}
        </button>
      ) : (
        <span className="aaactionmuted" title={action.note}>{action.label}</span>
      )}
      {action.detail ? <span className="aaactiondetail" title={action.note}>{action.detail}</span> : null}
    </span>
  );
}

function actionColumn(onAct: (accountId: string) => void): BookColumn {
  return {
    key: 'row_action',
    label: 'Action',
    width: '150px',
    hint: 'What you can do with an available account. Hover a muted label for why there is no button.',
    // Live actions first, then by label, so "Requested" rows sit together.
    sortValue: row => {
      const action = asRow(row).row_action;
      if (!action) return null;
      return (action.enabled ? '0' : '1') + action.label;
    },
    accessor: row => asRow(row).row_action?.label ?? '',
    cell: row => <ActionCell row={asRow(row)} onAct={onAct} />,
  };
}

// The board's columns this table shows, in this order. # · Fit · Engmt only with scores on.
const SCORE_KEYS = ['rank', 'fit_score', 'engagement_score'];
const LEADING_KEYS = ['account'];
const TRAILING_KEYS = ['account_owner', 'vertical', 'relationship_pill', 'why_now', 'credits_30'];

export function allAccountsColumns(
  scoresShown: boolean, poolsShown: boolean, withAction: boolean, onAct: (accountId: string) => void,
): BookColumn[] {
  const board = boardV2Columns(true);
  const byKey = new Map(board.map(column => [column.key, column]));
  const pick = (keys: string[]) => keys.map(key => byKey.get(key)).filter((column): column is BookColumn => Boolean(column));

  // The AE column's header names what it is here, where Owner is the SDR side.
  // Why now gets the width the narrower Owner column gives back.
  const trailing = pick(TRAILING_KEYS).map(column => {
    if (column.key === 'account_owner') return { ...column, label: 'AE' };
    if (column.key === 'why_now') return { ...column, width: '330px' };
    return column;
  });
  const columns = [
    ...(scoresShown ? pick(SCORE_KEYS) : []),
    ...pick(LEADING_KEYS),
    OWNER_COLUMN,
    ...(poolsShown ? [POOL_COLUMN] : []),
    EMPLOYEE_COUNT_COLUMN,
    ...trailing,
  ];
  return withAction ? [...columns, actionColumn(onAct)] : columns;
}
